import type { HeroId } from '../types';
import { HEROES } from '../heroes';
import { POWERUPS, autoLoadout, powerIsFree, powerupById, type PowerupId } from '../powerups';
import { pushLedger } from './ledger';
import { CRITTER_GATES, RIBBON_HONEY, UNLOCK_PER_COST } from './tables';
import type { Profile } from './types';

const CLASS_HERO: Record<string, HeroId> = {
  guardian: 'pip',
  infiltration: 'bramble',
  stealth: 'cleo',
  tidecaller: 'otto',
};

const KIT_IDS = new Set<string>(
  (Object.keys(HEROES) as HeroId[]).flatMap((id) => autoLoadout(id)),
);

/** 1 + up to two discounts, and never a level that would sit on the floor of 3. */
export function maxLevel(catalogCost: number): number {
  return 1 + Math.min(2, Math.max(0, catalogCost - 3));
}

/** Charge cap for an owned trick. Level 1 is the catalog. Each further level is one cheaper, floor 3. */
export function chargeCap(catalogCost: number, level: number): number {
  return Math.max(3, catalogCost - (level - 1));
}

export function isKit(id: string): boolean {
  return KIT_IDS.has(id);
}

/**
 * The trophy road grants every trick. Acorn prices are not on top of that gate.
 * Grove is equipped by Grove heroes and is not sold or upgraded, so the casual
 * curve does not spend its last acorns on a fifth class.
 */
export function isShopTrick(_id: string): boolean {
  return false;
}

export function offeredTricks(): readonly { id: string; cost: number; classId: string }[] {
  return POWERUPS.filter((power) => power.classId !== 'grove' && !powerIsFree(power));
}

export function unlockCost(id: string): number | null {
  if (!isShopTrick(id)) return null;
  const def = powerupById(id);
  if (!def) return null;
  return def.cost * UNLOCK_PER_COST;
}

/**
 * Trick levels are closed. Old saves are refunded in `migrateEconomy`.
 * The constants stay so that refund can name the same prices.
 */
export function upgradeCost(_id: string, _level: number): number | null {
  return null;
}

export function critterGate(id: HeroId): number {
  return CRITTER_GATES.find((gate) => gate.id === id)?.at ?? Number.POSITIVE_INFINITY;
}

export function nextCritterGate(best: number): { id: HeroId; at: number } | null {
  return CRITTER_GATES.find((gate) => gate.at > best) ?? null;
}

function classOpen(profile: Profile, classId: string): boolean {
  if (classId === 'universal') return true;
  const hero = CLASS_HERO[classId];
  if (!hero) return false;
  return profile.critters[hero]?.unlocked === true;
}

export interface ShopResult {
  ok: boolean;
  reason?: 'not-for-sale' | 'owned' | 'locked' | 'acorns' | 'honey' | 'capped' | 'missing';
}

export function buyTrick(profile: Profile, id: string, now: number): ShopResult {
  const cost = unlockCost(id);
  const def = powerupById(id);
  if (cost === null || !def) return { ok: false, reason: 'not-for-sale' };
  if (profile.tricks[id]) return { ok: false, reason: 'owned' };
  if (!classOpen(profile, def.classId)) return { ok: false, reason: 'locked' };
  if (profile.wallet.acorns < cost) return { ok: false, reason: 'acorns' };
  profile.wallet.acorns -= cost;
  profile.tricks[id] = { level: 1 };
  pushLedger(profile, now, 'unlock', { acorns: -cost }, 0, id);
  return { ok: true };
}

export function upgradeTrick(profile: Profile, id: string, now: number): ShopResult {
  const owned = profile.tricks[id];
  if (!owned) return { ok: false, reason: 'missing' };
  const price = upgradeCost(id, owned.level);
  if (price === null) return { ok: false, reason: 'capped' };
  if (profile.wallet.acorns < price) return { ok: false, reason: 'acorns' };
  profile.wallet.acorns -= price;
  owned.level += 1;
  pushLedger(profile, now, 'upgrade', { acorns: -price }, 0, id);
  return { ok: true };
}

export function buyRibbon(profile: Profile, now: number): ShopResult {
  if (profile.cosmetics.ribbon) return { ok: false, reason: 'owned' };
  if (profile.wallet.honey < RIBBON_HONEY) return { ok: false, reason: 'honey' };
  profile.wallet.honey -= RIBBON_HONEY;
  profile.cosmetics.ribbon = true;
  pushLedger(profile, now, 'cosmetic', { honey: -RIBBON_HONEY }, 0, 'ribbon');
  return { ok: true };
}

/**
 * Seat 0 caps that differ from the catalog. Empty means the match stays on catalog costs.
 * Quick matches should not pass this. A format override still wins inside trickCost.
 */
export function seatTrickCosts(profile: Pick<Profile, 'tricks'>): Record<string, number> | null {
  const out: Record<string, number> = {};
  for (const [id, owned] of Object.entries(profile.tricks)) {
    if (!owned) continue;
    const def = powerupById(id);
    if (!def) continue;
    const cap = chargeCap(def.cost, owned.level);
    if (cap !== def.cost) out[id] = cap;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function kitIds(heroId: HeroId): PowerupId[] {
  return autoLoadout(heroId);
}
